import { FormikProps, FormikValues } from "formik";
import {
  emptyNamedSerializableFile,
  parseDateToSend,
  withPhonePlus,
} from ".";
import { investmentTools } from "../i18n/securities/options";
import {
  HolderData,
  onUpdateSecuritiesData,
} from "../reducers/securitiesReducer";
import {
  BeneficiaryFields,
  BeneficiarySetupFields,
  GenerateBeneficiarySetupFields,
  HolderInformation,
  SecuritiesFormikValues,
  StepEndpointSecurities,
  StepFiveSecurities,
  StepFourSecurities,
  StepOneSecurities,
  StepSevenSecurities,
  StepSixSecurities,
  StepThreeSecurities,
  StepTwoSecurities,
} from "../types";
import { AppDispatch, RootState } from "../app/store";
import { Dispatch, SetStateAction } from "react";
import { getDraft } from "../connectors/connectors";
import { doc, getDoc, getFirestore } from "firebase/firestore";

export const getHoldersInformation = ({
  holdersData,
  language,
}: {
  holdersData: HolderData[];
  language: "en" | "es";
}) => {
  const holdersInformation: StepSevenSecurities["holders"] = [];

  const firstHolder = holdersData.find((holder) => holder.currentHolder === 1);

  // filter out the first holder, send additional holders only
  const filteredHoldersData = holdersData.filter(
    (holder) => holder.currentHolder !== 1
  );

  filteredHoldersData.forEach((holder) => {
    if (!holder.formValues) return;
    const { USCitizen, formValues, taxId } = holder;
    const values = formValues as SecuritiesFormikValues["stepOne"] &
      SecuritiesFormikValues["stepTwo"] &
      SecuritiesFormikValues["stepThree"] &
      SecuritiesFormikValues["stepFour"];
    const {
      //personal information
      name,
      lastName,
      motherName,
      gender,
      countryOfBirth,
      birthDate,
      civilStatus,
      educationLevel,
      dependants,
      mobilePhoneAR = "",
      mobilePhoneUS = "",
      homePhoneAR = "",
      homePhoneUS = "",
      workPhoneAR = "",
      workPhoneUS = "",
      email,
      addressesSameAsFirstHolders = false,
      taxAddresses,
      postalAddressDifferent,
      postalAddresses,
      //residence and identity information
      USResident,
      residenceCountry,
      firstCitizenship,
      additionalCitizenshipBool,
      additionalCitizenship,
      documentTypeAR = "DNI",
      documentTypeUS = "PASSPORT",
      documentNumber,
      documentCountry,
      documentEmisionDate,
      documentExpirationDate,
      //work and financial information
      employmentStatus,
      occupation,
      yearsWorked,
      companyType,
      previousOccupation,
      yearsPreviouslyWorked,
      companyName = "",
      companyCountry = "",
      companyAddresses,
      // financial information
      annualIncomeUpperBound,
      annualIncomeLowerBound,
      netIncomeLowerBound,
      netIncomeUpperBound,
      netWorthUpperBound,
      netWorthLowerBound,
      // investment knowledge
      investmentsExperience,
      otherInvestmentAccountName,
      hasOtherInvestmentAccountsBool,
      otherInvestmentAccountYears,
      investmentTools: investmentToolsValues,
      isAffiliatedWithBroker,
      brokerName,
      brokerEmployeeName,
      brokerEmployeeRelationship,
    } = values;

    const instruments: HolderInformation["investmentKnowledge"]["instruments"] =
      investmentTools[language]
        .filter(({ name }) => {
          // filter by boolean fields
          return !!investmentToolsValues?.[name]?.bool;
        })
        .reduce((acc, { value, name }) => {
          // map to instruments
          if (!acc) return acc;
          acc[value] = {
            knowledgeLevel: investmentToolsValues?.[name]?.knowlage ?? "NONE",
            since: Number.parseInt(investmentToolsValues?.[name]?.years ?? "0"),
          };
          return acc;
        }, {} as HolderInformation["investmentKnowledge"]["instruments"]);

    const holderTaxDomicile: HolderInformation["personalInformation"]["taxDomicile"] =
      {
        address: taxAddresses.fullAddress,
        postalCode: taxAddresses.zipCode,
        city: taxAddresses.city,
        province: taxAddresses.state,
        country: taxAddresses.country,
        ...(taxAddresses.floor ? { floor: taxAddresses.floor } : {}),
        ...(taxAddresses.apartment
          ? { apartment: taxAddresses.apartment }
          : {}),
      };

    const holderPostalDomicile: HolderInformation["personalInformation"]["postalDomicile"] =
      postalAddressDifferent && postalAddresses
        ? {
            address: postalAddresses?.fullAddress,
            postalCode: postalAddresses?.zipCode,
            city: postalAddresses?.city,
            province: postalAddresses?.state,
            country: postalAddresses?.country,
            ...(postalAddresses.floor ? { floor: postalAddresses.floor } : {}),
            ...(postalAddresses.apartment
              ? { apartment: postalAddresses.apartment }
              : {}),
          }
        : holderTaxDomicile;

    const holderStep: HolderInformation = {
      taxNumber: taxId,
      personalInformation: {
        isAmericanCitizen: USCitizen,
        email,
        completeName: `${name} ${lastName}`,
        motherLastName: motherName,
        gender,
        countryOfBirth,
        birthDate: parseDateToSend(birthDate),
        civilStatus,
        cellPhone: withPhonePlus(USCitizen ? mobilePhoneUS : mobilePhoneAR),
        ...(homePhoneUS || homePhoneAR
          ? { landline: withPhonePlus(USCitizen ? homePhoneUS : homePhoneAR) }
          : {}),
        ...(workPhoneUS || workPhoneAR
          ? { workPhone: withPhonePlus(USCitizen ? workPhoneUS : workPhoneAR) }
          : {}),
        educationLevel,
        numberOfDependants: parseInt(dependants),
        isSameAddress: addressesSameAsFirstHolders
          ? !firstHolder?.formValues?.postalAddressDifferent
          : !postalAddressDifferent,
        taxDomicile: holderTaxDomicile,
        postalDomicile: holderPostalDomicile,
      },
      residence: {
        isAmericanResident: USResident === "SI",
        fiscalResidenceCountry: residenceCountry,
        primaryCitizenship: firstCitizenship,
        hasAdditionalCitizenship: additionalCitizenshipBool === "SI",
        additionalCitizenship:
          additionalCitizenshipBool === "SI"
            ? additionalCitizenship
            : undefined,
      },
      identityInformation: {
        identificationType: USCitizen ? documentTypeUS : documentTypeAR,
        number: documentNumber?.toString() || "",
        documentIssuingCountry: documentCountry,
        documentIssuingDate: parseDateToSend(documentEmisionDate),
        documentExpirationDate: parseDateToSend(documentExpirationDate),
      },
      workInformation: {
        // Backend expects the WorkSituation enum or null, never an empty string
        workSituation: employmentStatus || null,
        currentOccupation: occupation,
        yearsInCurrentOccupation: Number.parseInt(yearsWorked ?? "0"),
        currentCompanyType: companyType,
        previousOccupation: previousOccupation,
        yearsInPreviousOccupation: Number.parseInt(
          yearsPreviouslyWorked ?? "0"
        ),
        ...(["EMPLOYEE", "RETIRED"].includes(employmentStatus)
          ? {
              employerInformation: {
                name: companyName,
                address: {
                  address: companyAddresses?.fullAddress || "",
                  postalCode: companyAddresses?.zipCode || "",
                  city: companyAddresses?.city || "",
                  province: companyAddresses?.state || "",
                  country: companyCountry,
                  ...(companyAddresses?.floor
                    ? { floor: companyAddresses.floor }
                    : {}),
                  ...(companyAddresses?.apartment
                    ? { apartment: companyAddresses.apartment }
                    : {}),
                },
              },
            }
          : {}),
        ...(employmentStatus === "INDEPENDENT"
          ? {
              employerInformation: {
                name: "SELF",
                address: holderTaxDomicile,
              },
            }
          : {}),
      },
      financialInformation: {
        annualIncomeFrom: Number.parseFloat(annualIncomeLowerBound),
        annualIncomeTo: Number.parseFloat(annualIncomeUpperBound),
        netIncomeFrom: Number.parseFloat(netIncomeLowerBound),
        netIncomeTo: Number.parseFloat(netIncomeUpperBound),
        liquidAssetsFrom: Number.parseFloat(netWorthLowerBound),
        liquidAssetsTo: Number.parseFloat(netWorthUpperBound),
      },
      investmentKnowledge: {
        knowledgeLevel: investmentsExperience,
        institutionName: otherInvestmentAccountName,
        haveAnotherInvestmentAccount: hasOtherInvestmentAccountsBool === "SI",
        yearsOfExperienceInAccount: Number.parseInt(
          otherInvestmentAccountYears ?? "0"
        ),
        instruments,
        ...(isAffiliatedWithBroker === "SI"
          ? {
              familyAffiliation: {
                brokerDealerName: brokerName as string,
                employeeName: brokerEmployeeName as string,
                relationship: brokerEmployeeRelationship as string,
              },
            }
          : {}),
      },
    };

    holdersInformation.push(holderStep);
  });

  return holdersInformation;
};

export const handleGetDraft = async ({
  draftUUID,
  formik,
  dispatch,
  setShowInfoStep,
  state,
  language,
}: {
  draftUUID: string;
  formik: FormikProps<FormikValues>;
  dispatch: AppDispatch;
  setShowInfoStep: Dispatch<SetStateAction<boolean>>;
  state: RootState;
  language: "en" | "es";
}) => {
  const parseDate = (date: string) => {
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  const db = getFirestore();

  try {
    const draftResponse = await getDraft({
      draftUUID,
    });

    const { steps: draftSteps } = draftResponse.data;

    if (draftSteps.length > 0) {
      const sendedSteps: StepEndpointSecurities[] = [];
      let USCitizen = state.securities.USCitizen;
      let formValues: FormikValues = { ...state.securities.formValues };

      // Get previously completed validation data, fall back to draft values
      const prevStep = {
        accountHolderSize:
          state.securities.accountHolders ?? draftSteps[0]?.accountHolderSize,
        referrer:
          state.securities.initialValidationData.referred ||
          draftSteps[0]?.referrer,
        referredByCommercialTeam:
          !!state.securities.initialValidationData.referred,
        taxNumber:
          state.securities.initialValidationData.taxId ??
          draftSteps[0]?.taxNumber,
        holdersAreMarried:
          state.securities.holdersMarried ?? draftSteps[0].holdersAreMarried,
      };

      // Find and process step one
      const stepOne = draftSteps.find(
        (step: StepOneSecurities) => step.personalInformation
      ) as StepOneSecurities;

      if (stepOne) {
        const {
          personalInformation: {
            completeName,
            motherLastName,
            gender,
            birthDate,
            civilStatus,
            educationLevel,
            cellPhone,
            landline,
            workPhone,
            email,
            taxDomicile,
            postalDomicile,
            isAmericanCitizen,
            countryOfBirth = "",
            numberOfDependants,
          },
        } = stepOne;

        // HACK: `stringify` is not the best way to do this
        const postalAddressDifferent =
          !!postalDomicile?.address &&
          JSON.stringify(taxDomicile) !== JSON.stringify(postalDomicile);

        USCitizen = isAmericanCitizen;

        const proofOfAddressRef = doc(db, "proofOfAddressImages", draftUUID);
        const proofOfAddressSnap = await getDoc(proofOfAddressRef);

        // Parse step one data
        const stepOneParsed: SecuritiesFormikValues["stepOne"] = {
          name: completeName,
          lastName: "",
          motherName: motherLastName,
          gender,
          birthDate: parseDate(birthDate),
          civilStatus,
          educationLevel,
          countryOfBirth,
          dependants: numberOfDependants.toString(),

          // Matter's phone field reads the country off the leading "+", so the
          // value keeps it instead of being stripped as it used to be.
          mobilePhoneAR: !isAmericanCitizen ? cellPhone : "",
          mobilePhoneUS: isAmericanCitizen ? cellPhone : "",
          homePhoneAR: !isAmericanCitizen ? landline : "",
          homePhoneUS: isAmericanCitizen ? landline : "",
          workPhoneAR: !isAmericanCitizen ? workPhone : "",
          workPhoneUS: isAmericanCitizen ? workPhone : "",

          email: email,

          addressesSameAsFirstHolders: false,

          taxAddresses: {
            country: taxDomicile.country,
            fullAddress: taxDomicile.address,
            zipCode: taxDomicile.postalCode,
            city: taxDomicile.city,
            state: taxDomicile.province,
            floor: taxDomicile.floor || "",
            apartment: taxDomicile.apartment || "",
          },

          postalAddressDifferent,
          ...(postalAddressDifferent && {
            postalAddresses: {
              country: postalDomicile.country,
              fullAddress: postalDomicile.address,
              zipCode: postalDomicile.postalCode,
              city: postalDomicile.city,
              state: postalDomicile.province,
              floor: postalDomicile.floor || "",
              apartment: postalDomicile.apartment || "",
            },
          }),
          proofOfAddress: proofOfAddressSnap.exists()
            ? emptyNamedSerializableFile(
                proofOfAddressSnap.data().proofOfAddress
              )
            : null,
        };

        // Create step one store object
        const stepOneToStore: StepOneSecurities = {
          ...prevStep,
          personalInformation: {
            ...stepOne.personalInformation,

            // HACK: Store step with parsed date, just like done on its `onContinue`
            birthDate: parseDateToSend(stepOneParsed.birthDate),

            // HACK: `isAmericanCitizen` is misplaced on GET draft response
            isAmericanCitizen,
          },
          "@type": "1_SECURITY",
        };

        sendedSteps.push(stepOneToStore);
        formValues = {
          ...formValues,
          ...stepOneParsed,
        };
      }

      // Find and process step two
      const stepTwo = draftSteps.find(
        (step: StepTwoSecurities) => step.identityInformation
      ) as StepTwoSecurities;

      if (stepTwo) {
        const {
          residence: {
            isAmericanResident,
            fiscalResidenceCountry,
            primaryCitizenship,
            hasAdditionalCitizenship,
            additionalCitizenship,
          },
          identityInformation: {
            identificationType,
            number,
            documentIssuingCountry,
            documentIssuingDate,
            documentExpirationDate,
          },
        } = stepTwo;

        const stepOne = draftSteps.find(
          (step: StepOneSecurities) => step.personalInformation
        ) as StepOneSecurities;

        const { taxNumber } = stepOne || {};

        const dniRef = doc(db, "dniImages", draftUUID);
        const dniSnap = await getDoc(dniRef);
        const passportRef = doc(db, "passportImages", draftUUID);
        const passportSnap = await getDoc(passportRef);

        // Parse step two data
        const stepTwoParsed: SecuritiesFormikValues["stepTwo"] = {
          USResident: isAmericanResident ? "SI" : "NO",
          residenceCountry: fiscalResidenceCountry || "",
          firstCitizenship: primaryCitizenship,
          additionalCitizenshipBool: hasAdditionalCitizenship ? "SI" : "NO",
          ...(hasAdditionalCitizenship && { additionalCitizenship }),
          documentTypeAR: !state.securities.USCitizen
            ? identificationType
            : undefined,
          documentTypeUS: state.securities.USCitizen
            ? identificationType
            : undefined,
          documentNumber: state.securities.USCitizen ? "" : number,
          passportNumber: state.securities.USCitizen ? number : "",
          documentCountry: documentIssuingCountry,
          documentEmisionDate: parseDate(documentIssuingDate),
          documentExpirationDate: parseDate(documentExpirationDate),
          socialSecurityNumber:
            state.securities.USCitizen && taxNumber ? taxNumber : "",
          taxId: !state.securities.USCitizen && taxNumber ? taxNumber : "",
          documentFrontDNI:
            identificationType == "DNI" && dniSnap.exists()
              ? emptyNamedSerializableFile(dniSnap.data().frontDNI)
              : undefined,
          documentBackDNI:
            identificationType == "DNI" && dniSnap.exists()
              ? emptyNamedSerializableFile(dniSnap.data().backDNI)
              : undefined,
          documentFrontPASSPORT:
            identificationType == "PASSPORT" && passportSnap.exists()
              ? emptyNamedSerializableFile(
                  passportSnap.data().firstPagePassport
                )
              : undefined,
          documentBackPASSPORT:
            identificationType == "PASSPORT" && passportSnap.exists()
              ? emptyNamedSerializableFile(
                  passportSnap.data().secondPagePassport
                )
              : undefined,
        };

        // Create step two store object
        const stepTwoToStore = {
          ...stepTwo,

          identityInformation: {
            ...stepTwo.identityInformation,
            // HACK: Store step with parsed date, just like done on its `onContinue`
            documentIssuingDate: parseDateToSend(
              stepTwoParsed.documentEmisionDate
            ),
            documentExpirationDate: parseDateToSend(
              stepTwoParsed.documentExpirationDate
            ),
          },

          "@type": "2_SECURITY",
        };

        sendedSteps.push(stepTwoToStore);
        formValues = {
          ...formValues,
          ...stepTwoParsed,
        };
      }

      // Find and process step three
      const stepThree = draftSteps.find(
        // HACK: `workInformation` is not present on GET draft response, but its sended when step is completed
        // so we need to check for both
        (step: StepThreeSecurities) =>
          step.workSituation || step.workInformation
      ) as StepThreeSecurities;

      if (stepThree) {
        const {
          workSituation: workSituationStep,
          workInformation,
          financialInformation: {
            annualIncomeFrom,
            annualIncomeTo,
            liquidAssetsFrom,
            liquidAssetsTo,
            netIncomeFrom,
            netIncomeTo,
          },
        } = stepThree;

        const {
          workSituation,
          currentOccupation,
          yearsInCurrentOccupation,
          currentCompanyType,
          previousOccupation,
          yearsInPreviousOccupation,
          employerInformation,
        } = workSituationStep || workInformation || {};

        // Parse step three data
        const stepThreeParsed = {
          employmentStatus: workSituation,
          occupation: currentOccupation || "",
          yearsWorked: yearsInCurrentOccupation?.toString() || "",
          companyType: currentCompanyType || "",
          previousOccupation: previousOccupation || "",
          yearsPreviouslyWorked: yearsInPreviousOccupation?.toString() || "",
          ...(employerInformation && {
            companyName: employerInformation.name,
            companyCountry: employerInformation.address.country,
            companyAddresses: {
              fullAddress: employerInformation.address.address,
              city: employerInformation.address.city,
              zipCode: employerInformation.address.postalCode,
              state: employerInformation.address.province,
              floor: employerInformation.address.floor || "",
              apartment: employerInformation.address.apartment || "",
            },
          }),
          annualIncomeLowerBound: annualIncomeFrom.toString(),
          annualIncomeUpperBound: annualIncomeTo.toString(),
          netIncomeLowerBound: netIncomeFrom.toString(),
          netIncomeUpperBound: netIncomeTo.toString(),
          netWorthLowerBound: liquidAssetsFrom.toString(),
          netWorthUpperBound: liquidAssetsTo.toString(),
        };

        // Create step three store object
        const stepThreeToStore: StepThreeSecurities = {
          ...stepThree,
          ...(workSituationStep && {
            workInformation: workSituationStep,
            workSituation: undefined,
          }),
          "@type": "3_SECURITY",
        };

        sendedSteps.push(stepThreeToStore);
        formValues = {
          ...formValues,
          ...stepThreeParsed,
        };
      }

      // Find and process step four
      const stepFour = draftSteps.find(
        (step: StepFourSecurities) => step.investmentKnowledge
      ) as StepFourSecurities;

      if (stepFour) {
        const {
          investmentKnowledge: {
            knowledgeLevel,
            instruments,
            haveAnotherInvestmentAccount,
            institutionName,
            yearsOfExperienceInAccount,
            familyAffiliation,
          },
        } = stepFour;

        // Parse step four data
        const stepFourParsed: SecuritiesFormikValues["stepFour"] = {
          investmentsExperience: knowledgeLevel || "NONE",
          // Parse investment tools from instruments
          investmentTools: investmentTools[language].reduce(
            (acc, { name, value }) => {
              if (instruments?.[value]) {
                acc[name] = {
                  bool: true,
                  knowlage: instruments?.[value]
                    ? instruments[value]?.knowledgeLevel
                    : "NONE",
                  years: instruments?.[value]
                    ? instruments[value]?.since.toString()
                    : "",
                };
              }
              return acc;
            },
            {} as SecuritiesFormikValues["stepFour"]["investmentTools"]
          ),
          hasOtherInvestmentAccountsBool: haveAnotherInvestmentAccount
            ? "SI"
            : "NO",
          ...(haveAnotherInvestmentAccount && {
            otherInvestmentAccountName: institutionName,
            otherInvestmentAccountYears: yearsOfExperienceInAccount?.toString(),
          }),
          isAffiliatedWithBroker: familyAffiliation ? "SI" : "NO",
          brokerName: familyAffiliation?.brokerDealerName,
          brokerEmployeeName: familyAffiliation?.employeeName,
          brokerEmployeeRelationship: familyAffiliation?.relationship,
        };

        // Create step four store object
        const stepFourToStore = {
          ...stepFour,
          "@type": "4_SECURITY",
        };

        sendedSteps.push(stepFourToStore);
        formValues = {
          ...formValues,
          ...stepFourParsed,
        };
      }

      // Find and process step five
      const stepFive = draftSteps.find(
        (step: StepFiveSecurities) => step.objectiveAndFunds
      ) as StepFiveSecurities;

      if (stepFive) {
        const {
          objectiveAndFunds: {
            objective,
            accountOpeningObjective,
            originOfFunds,
            otherFundOrigin,
            fundingPlan,
            initialTransferAmount,
            fundingSourceAccountHolder,
            fundingSourceBankName,
            timeHorizon,
          },
          riskProfile,
          transfersAndWithdrawals: {
            regularWithdrawals,
            withdrawalReason,
            otherWithdrawalReason,
            withdrawalsFrequency,
            otherWithdrawalsFrequency,
            thirdPartyTransfers,
            transferFrequency,
            otherTransferFrequency,
            thirdPartyTransfersReason,
            otherThirdPartyTransfersReason,
          },
          optionsAndMargins: {
            interestedInMargins,
            interestedInOptions,
            investmentObjectives,
          },
          interestedInAssigningBeneficiaries,
          beneficiaries = [],
        } = stepFive;

        const beneficiariesKeys = Array.from(
          { length: beneficiaries?.length || 0 },
          (_, index) => `beneficiary${index + 1}`
        ) as (keyof BeneficiarySetupFields<1 | 2 | 3 | 4 | 5>)[];

        // Parse step five data
        const stepFiveParsed: SecuritiesFormikValues["stepFiveFirstSubStep"] = {
          accountObjective: objective,
          accountOpeningObjective,
          originOfFunds: originOfFunds,
          ...(otherFundOrigin && { originOfFundsOther: otherFundOrigin }),
          fundingDate: fundingPlan.toString(),
          initialTransferAmount: initialTransferAmount.toString(),
          fundingSourceAccountHolder,
          fundingSourceBankName,
          timeHorizon,
          riskProfile,
          regularWithdrawals: {
            bool: regularWithdrawals ? "SI" : "NO",
            ...(withdrawalReason && {
              reason: withdrawalReason,
            }),
            ...(otherWithdrawalReason && {
              otherReason: otherWithdrawalReason,
            }),
            ...(withdrawalsFrequency && {
              frequency: withdrawalsFrequency,
            }),
            ...(otherWithdrawalsFrequency && {
              otherFrequency: otherWithdrawalsFrequency,
            }),
          },
          thirdPartyTransfers: {
            bool: thirdPartyTransfers ? "SI" : "NO",
            ...(transferFrequency && {
              frequency: transferFrequency,
            }),
            ...(otherTransferFrequency && {
              otherFrequency: otherTransferFrequency,
            }),
            ...(thirdPartyTransfersReason && {
              reason: thirdPartyTransfersReason,
            }),
            ...(otherThirdPartyTransfersReason && {
              otherReason: otherThirdPartyTransfersReason,
            }),
          },
          tradeOptions: {
            bool: interestedInOptions ? "SI" : "NO",
            ...(investmentObjectives?.length && investmentObjectives.length > 0
              ? {
                  options: investmentObjectives,
                }
              : {}),
          },
          tradeMargins: {
            bool: interestedInMargins ? "SI" : "NO",
          },
          beneficiariesBool: interestedInAssigningBeneficiaries ? "SI" : "NO",
          ...beneficiariesKeys.reduce((acc, key, index) => {
            const percentageKey: keyof GenerateBeneficiarySetupFields<
              1 | 2 | 3 | 4 | 5
            > = `${key}Percentage`;
            acc[key] = {
              name: beneficiaries?.[index].name,
            };
            return {
              ...acc,
              [percentageKey]:
                beneficiaries?.[index].assignedPercentage.toString(),
            };
          }, {} as BeneficiarySetupFields<1 | 2 | 3 | 4 | 5>),
        };

        // Create step five store object
        const stepFiveToStore = {
          ...stepFive,
          "@type": "5_SECURITY",
        };

        sendedSteps.push(stepFiveToStore);
        formValues = {
          ...formValues,
          ...stepFiveParsed,
        };
      }

      // Find and process step six (beneficiaries info)
      const stepSix = draftSteps.find(
        (step: StepSixSecurities) => step.beneficiariesInfo
      ) as StepSixSecurities;

      if (stepSix) {
        const { beneficiariesInfo } = stepSix;

        // Parse step six data
        const stepSixParsed: BeneficiaryFields<1 | 2 | 3 | 4 | 5> =
          beneficiariesInfo.reduce(
            (acc, beneficiary, index) => ({
              ...acc,
              [`beneficiary${index + 1}`]: {
                name: beneficiary.name,
                percentage: beneficiary.assignedPercentage.toString(),
                gender: beneficiary.gender,
                birthDate: parseDate(beneficiary.birthDate),
                email: beneficiary.email,
                phoneAR: !state.securities.USCitizen
                  ? beneficiary.cellPhone
                  : undefined,
                phoneUS: state.securities.USCitizen
                  ? beneficiary.cellPhone
                  : undefined,

                taxIdType: beneficiary.taxType,
                taxIdNumber: beneficiary.taxNumber,
                socialSecurityNumber: beneficiary.taxNumber,
                relationship: beneficiary.relationshipWithBeneficiary,
                country: beneficiary.domicile.country,
                fullAddress: beneficiary.domicile.address,
                city: beneficiary.domicile.city,
                zipCode: beneficiary.domicile.postalCode,
                state: beneficiary.domicile.province,
                floor: beneficiary.domicile.floor || "",
                apartment: beneficiary.domicile.apartment || "",
              },
            }),
            {} as BeneficiaryFields<1 | 2 | 3 | 4 | 5>
          );

        // Create step six store object
        const stepSixToStore: StepSixSecurities = {
          ...stepSix,
          beneficiariesInfo: beneficiariesInfo.map((beneficiary, index) => ({
            ...beneficiary,
            birthDate: parseDateToSend(
              stepSixParsed[
                `beneficiary${index + 1}` as keyof BeneficiaryFields<
                  1 | 2 | 3 | 4 | 5
                >
              ].birthDate
            ),
          })),
          "@type": "6_SECURITY",
        };

        sendedSteps.push(stepSixToStore);
        formValues = {
          ...formValues,
          ...stepSixParsed,
        };
      }

      // Update formik values with the parsed data
      await formik.setValues(
        (prevValues) => ({
          ...prevValues,
          ...formValues,
        }),
        false
      );

      // await to update state
      new Promise((resolve) => {
        setShowInfoStep(true);
        dispatch(
          onUpdateSecuritiesData({
            sendedSteps,
            formValues,
            USCitizen,
          })
        );
        resolve(true);
      });
    } else {
      setShowInfoStep(false);
    }
  } catch (error) {
    console.log("Error on getDraft", error);
  }
};
